<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { isSmallViewport } from 'dashboard/store/modules/conversations/helpers/actionHelpers';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';

import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import InboxEmptyState from './InboxEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const isConversationLoading = ref(false);
const conversationLoadError = ref(false);

const notification = useMapGetter('notifications/getFilteredNotifications');
const currentChat = useMapGetter('getSelectedChat');
const conversationById = useMapGetter('getConversationById');
const uiFlags = useMapGetter('notifications/getUIFlags');

const inboxId = computed(() => Number(route.params.inboxId));
const conversationId = computed(() => Number(route.params.id));

const activeSortOrder = computed(() => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { sort_by: sortBy } = filterBy;
  return sortBy || 'desc';
});

const notifications = computed(() => {
  return notification.value({
    sortOrder: activeSortOrder.value,
  });
});

const showEmptyState = computed(() => {
  return (
    !conversationId.value ||
    (!notifications.value?.length && uiFlags.value.isFetching)
  );
});

const isContactPanelOpen = computed(() => {
  if (currentChat.value.id) {
    const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
    return isContactSidebarOpen;
  }
  return false;
});

const closeContactPanel = () => {
  if (
    !uiSettings.value?.is_contact_sidebar_open &&
    !uiSettings.value?.is_copilot_panel_open
  ) {
    return;
  }

  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const findConversation = () => {
  return conversationById.value(conversationId.value);
};

const notifyActiveConversationLoadStart = activeConversationId => {
  if (typeof window === 'undefined' || !activeConversationId) {
    return;
  }

  window.startZoraxyActiveConversationLoad?.({
    targetCount: 1,
    conversationId: activeConversationId,
  });
};

const notifyActiveConversationLoadDone = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.finishZoraxyActiveConversationLoad?.();
};

const hydrateConversationHistory = activeConversationId => {
  if (isSmallViewport()) {
    return Promise.resolve(null);
  }

  return store
    .dispatch('hydrateConversationHistoryFully', activeConversationId)
    .catch(() => null);
};

const setActiveChat = async () => {
  if (!conversationId.value) {
    notifyActiveConversationLoadDone();
    return false;
  }

  if (
    currentChat.value?.id === conversationId.value &&
    currentChat.value?.conversationDetailsFetched === true
  ) {
    await hydrateConversationHistory(conversationId.value);
    notifyActiveConversationLoadDone();
    return true;
  }

  notifyActiveConversationLoadStart(conversationId.value);
  const selectedConversation = findConversation();
  const hasPrefetchedConversation =
    selectedConversation?.conversationDetailsFetched === true;
  const canOpenOptimistically =
    selectedConversation && selectedConversation.id !== currentChat.value?.id;

  if (canOpenOptimistically) {
    await store.dispatch('previewActiveChat', { data: selectedConversation });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
  }

  if (hasPrefetchedConversation && selectedConversation) {
    try {
      await store.dispatch('setActiveChat', { data: selectedConversation });
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      await hydrateConversationHistory(conversationId.value);
    } finally {
      notifyActiveConversationLoadDone();
    }

    return true;
  }

  try {
    const loadedConversation = await store.dispatch(
      'getConversation',
      conversationId.value
    );
    if (!loadedConversation) return false;
  } catch {
    return false;
  }

  try {
    const hydratedConversation = findConversation();
    if (!hydratedConversation) return false;

    if (
      hydratedConversation.id !== currentChat.value?.id ||
      hydratedConversation.conversationDetailsFetched !== true
    ) {
      await store.dispatch('setActiveChat', { data: hydratedConversation });
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    }

    await hydrateConversationHistory(conversationId.value);
    return true;
  } catch {
    return false;
  } finally {
    notifyActiveConversationLoadDone();
  }
};

const fetchConversationById = async () => {
  if (!conversationId.value) return;

  const existingChat = findConversation();
  isConversationLoading.value = !existingChat;
  conversationLoadError.value = false;

  try {
    const loaded = await setActiveChat();
    if (!loaded) conversationLoadError.value = true;
  } catch {
    conversationLoadError.value = true;
  } finally {
    isConversationLoading.value = false;
  }
};

const retryConversationLoad = () => {
  if (conversationId.value) fetchConversationById();
};

watch(
  conversationId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      closeContactPanel();
      fetchConversationById();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="h-full w-full flex-1">
    <div v-if="showEmptyState" class="flex w-full h-full">
      <InboxEmptyState />
    </div>
    <div v-else class="flex flex-col w-full h-full">
      <div
        v-if="conversationLoadError"
        class="mx-4 mt-3 flex items-center justify-between gap-3 rounded-lg border border-n-amber-7/30 bg-n-amber-2 px-4 py-3 text-sm text-n-slate-12"
        role="alert"
      >
        <span>{{ $t('INBOX.LIST.CONVERSATION_LOAD_ERROR') }}</span>
        <button
          type="button"
          class="flex-shrink-0 rounded-md border border-n-amber-7/40 px-3 py-1.5 font-medium hover:bg-n-amber-3"
          @click="retryConversationLoad"
        >
          {{ $t('INBOX.LIST.RETRY_CONVERSATION_LOAD') }}
        </button>
      </div>
      <div
        v-if="isConversationLoading"
        class="flex items-center flex-1 my-4 justify-center bg-n-solid-1"
      >
        <Spinner class="text-n-brand" />
      </div>
      <div v-else class="flex h-full min-w-0">
        <ConversationBox
          class="flex-1 [&.conversation-details-wrap]:!border-0"
          is-inbox-view
          :inbox-id="inboxId"
          :is-on-expanded-layout="false"
        >
          <SidepanelSwitch v-if="currentChat.id" />
        </ConversationBox>
        <ConversationSidebar
          v-if="isContactPanelOpen"
          :current-chat="currentChat"
        />
      </div>
    </div>
  </div>
</template>
